import React from "react";
import Bugsnag from "@bugsnag/js";
import useRecipes from "../../../../../hooks/useRecipes";
import useOrganizationData from "../../../../../hooks/useOrganizationData";

const useRecipeDetails = (recipe, organizationId, uploadImage) => {
  const organizationData = useOrganizationData(organizationId);

  // Get file upload limit from organization data with default fallback
  const defaultLimit = 1000000;
  const fileUploadLimit = React.useMemo(() => {
    if (organizationData?.account?.fileUploadLimit) {
      return organizationData.account.fileUploadLimit;
    }
    return defaultLimit;
  }, [organizationData?.account?.fileUploadLimit]);

  // Only get editRecipe and editIngredient functions, not the ingredients data
  const { editRecipe, editIngredient } = useRecipes({
    organizationId,
    skipIngredientsLoad: true, // Add flag to skip ingredients loading
  });

  const [showDialog, setShowDialog] = React.useState(false);
  const [selectedRecipe, setSelectedRecipe] = React.useState(null);

  // Reset selected recipe if main recipe changes
  React.useEffect(() => {
    if (recipe) {
      setSelectedRecipe(null);
    }
  }, [recipe?.id]);

  // Set recipe for editing
  const handleEditClick = React.useCallback(() => {
    if (!recipe) return;
    setSelectedRecipe(recipe);
    setShowDialog(true);
  }, [recipe]);

  // Handle image upload
  const handleImageUpload = React.useCallback(
    async (e) => {
      if (!recipe || !e.target.files?.[0]) return;

      const image = e.target.files[0];

      if (image.size > fileUploadLimit) {
        alert(`Image must be less than ${fileUploadLimit / 1000000}MB`);
        return;
      }

      await uploadImage({
        image,
        recipe,
        onStart: () => {},
        onProgress: () => {},
        onCompleted: () => {},
      });
    },
    [recipe, uploadImage, fileUploadLimit],
  );

  // Close dialog and handle recipe update
  const handleDialogClose = React.useCallback(
    (updatedRecipe, imageURL) => {
      if (updatedRecipe) {
        editRecipe(updatedRecipe).then(() => {
          if (imageURL && imageURL.size < fileUploadLimit) {
            uploadImage({ image: imageURL, recipe: updatedRecipe });
          } else if (imageURL) {
            Bugsnag.notify("Image too large");
          }
        });
      }
      setShowDialog(false);
    },
    [editRecipe, uploadImage, fileUploadLimit],
  );

  return {
    showDialog,
    selectedRecipe,
    editIngredient,
    handleEditClick,
    handleImageUpload,
    handleDialogClose,
  };
};

export default useRecipeDetails;
